/**
 * Handles two URL patterns:
 *   /repair-database/:deviceSlug/:boardOrCase
 *
 * If :boardOrCase matches a board revision (by name) for :deviceSlug → render unified device page
 *   with that revision pre-selected.
 * Otherwise → look up as a repair case slug → show case.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import {
  fetchTaxonomy,
  fetchRepairCase,
  fetchLinkedReferences,
  findFamily,
  findManufacturer,
  revisionsForFamily,
  type KbTaxonomy,
  RESULT_OPTIONS,
} from '../lib/repair-kb-client';
import type { RepairCase, RepairMedia, LinkedReference } from '../lib/repair-kb-client';
import RepairDevicePage from './RepairDevicePage';
import { ChevronRight, Loader, Calendar, X } from '../lib/icons';

// ── Helpers ───────────────────────────────────────────────────────────────────

function resultLabel(v: string) { return RESULT_OPTIONS.find(r => r.value === v)?.label ?? v; }
function resultColor(v: string) {
  switch (v) {
    case 'repaired':           return 'text-green-400 bg-green-900/30 border-green-800';
    case 'partially-repaired': return 'text-yellow-400 bg-yellow-900/30 border-yellow-800';
    case 'no-fix':             return 'text-red-400 bg-red-900/30 border-red-800';
    case 'board-donor':        return 'text-orange-400 bg-orange-900/30 border-orange-800';
    default:                   return 'text-gray-400 bg-gray-800 border-gray-700';
  }
}
function formatDate(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
function prose(text: string) {
  return text.split('\n').filter(Boolean).map((line, i) => (
    <p key={i} className="text-gray-300 leading-relaxed mb-3 last:mb-0">{line}</p>
  ));
}

// ── Case view (repair case slug) ─────────────────────────────────────────────

function CasePage({
  taxonomy, deviceSlug, repairCase,
}: {
  taxonomy:    KbTaxonomy;
  deviceSlug:  string;
  repairCase:  RepairCase;
}) {
  const c      = repairCase;
  const family = findFamily(taxonomy, deviceSlug);
  const mfr    = family ? findManufacturer(taxonomy, family.manufacturer_id) : null;
  const [linked, setLinked] = useState<LinkedReference[]>([]);
  const [lightbox, setLightbox] = useState<RepairMedia | null>(null);

  useEffect(() => {
    if (c.id) fetchLinkedReferences(c.id, false).then(setLinked).catch(() => {});
  }, [c.id]);

  const sortedMedia = [...(c.repair_case_media ?? [])].sort((a, b) => {
    if (a.is_featured && !b.is_featured) return -1;
    if (!a.is_featured && b.is_featured) return 1;
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });

  const canonicalUrl = `https://gamefixsolutions.com/repair-database/${deviceSlug}/${c.slug}`;

  return (
    <div className="bg-[#0D0D0D] min-h-screen text-[#E0E0E0]">
      <SEOHead
        title={`${c.seo_title || c.title || 'Repair Case'} | GameFix Solutions`}
        description={c.seo_description || c.repair_summary.slice(0, 155) || `Board-level repair case for ${family?.name ?? deviceSlug}.`}
        canonical={canonicalUrl}
      />

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-gray-400 hover:text-white"><X className="h-6 w-6" /></button>
          <img src={lightbox.public_url} alt={lightbox.caption} className="max-w-full max-h-[90vh] object-contain rounded-lg" />
          {lightbox.caption && <p className="absolute bottom-6 text-sm text-gray-400">{lightbox.caption}</p>}
        </div>
      )}

      {/* Breadcrumb */}
      <div className="border-b border-gray-800/60">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
            <Link to="/" className="hover:text-gray-300">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/repair-database" className="hover:text-gray-300">Repair Database</Link>
            {mfr && <><ChevronRight className="h-3 w-3" /><span>{mfr.name}</span></>}
            {family && <><ChevronRight className="h-3 w-3" /><Link to={`/repair-database/${deviceSlug}`} className="hover:text-gray-300">{family.name}</Link></>}
            <ChevronRight className="h-3 w-3" />
            <span className="text-[#E0E0E0] truncate max-w-[200px]">{c.title || 'Repair Case'}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            {family && <span className="text-sm font-semibold text-[#FF1744]">{family.name}</span>}
            {c.result && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${resultColor(c.result)}`}>
                {resultLabel(c.result)}
              </span>
            )}
          </div>
          <h1 className="text-2xl md:text-3xl font-black text-white mb-3 leading-tight">
            {c.title || (family ? `${family.name} Repair` : 'Repair Case')}
          </h1>
          {c.symptoms_summary.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {c.symptoms_summary.map(s => (
                <span key={s} className="text-xs bg-gray-800 text-gray-400 px-3 py-1 rounded-full">{s}</span>
              ))}
            </div>
          )}
          {c.published_at && (
            <p className="text-xs text-gray-600 flex items-center gap-1">
              <Calendar className="h-3 w-3" />Published {formatDate(c.published_at)}
            </p>
          )}
        </div>

        {/* Media gallery */}
        {sortedMedia.length > 0 && (
          <div className={`grid gap-3 mb-8 ${sortedMedia.length === 1 ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3'}`}>
            {sortedMedia.map(m => (
              <button key={m.id} onClick={() => setLightbox(m)}
                className="aspect-video bg-gray-900 rounded-xl overflow-hidden hover:opacity-90 transition-opacity">
                <img src={m.public_url} alt={m.caption || ''} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="space-y-8">
          {c.detailed_symptoms && (
            <section>
              <h2 className="text-xs font-semibold text-gray-500 tracking-widest uppercase mb-3">Symptoms</h2>
              <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-800">{prose(c.detailed_symptoms)}</div>
            </section>
          )}
          {c.repair_summary && (
            <section>
              <h2 className="text-xs font-semibold text-gray-500 tracking-widest uppercase mb-3">Repair Summary</h2>
              <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-800">{prose(c.repair_summary)}</div>
            </section>
          )}
          {c.root_cause && (
            <section>
              <h2 className="text-xs font-semibold text-gray-500 tracking-widest uppercase mb-3">Root Cause</h2>
              <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-800">{prose(c.root_cause)}</div>
            </section>
          )}
          {c.components_replaced && (
            <section>
              <h2 className="text-xs font-semibold text-gray-500 tracking-widest uppercase mb-3">Components Replaced</h2>
              <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-800">{prose(c.components_replaced)}</div>
            </section>
          )}
          {c.public_notes && (
            <section>
              <h2 className="text-xs font-semibold text-gray-500 tracking-widest uppercase mb-3">Notes</h2>
              <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-800">{prose(c.public_notes)}</div>
            </section>
          )}

          {/* Linked references */}
          {linked.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold text-gray-500 tracking-widest uppercase mb-3">Related References</h2>
              <div className="space-y-2">
                {linked.map(r => (
                  <Link key={r.id} to={`/repair-database/ref/${r.slug ?? r.id}`}
                    className="flex items-center justify-between bg-gray-900 border border-gray-800 hover:border-[#00BFFF]/40 rounded-xl px-4 py-3 transition-all group">
                    <span className="text-sm text-[#E0E0E0] group-hover:text-white">{r.title}</span>
                    <ChevronRight className="h-4 w-4 text-gray-600 group-hover:text-[#00BFFF]" />
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* CTA */}
        <div className="border-t border-gray-800 pt-8 mt-10 text-center">
          <p className="text-gray-400 text-sm mb-1">Have this problem with your {family?.name ?? 'console'}?</p>
          <p className="text-gray-600 text-xs mb-5">GameFix Solutions accepts mail-in repairs nationwide.</p>
          <Link to="/mail-in-repairs"
            className="inline-flex items-center gap-2 bg-[#FF1744] hover:bg-[#D50000] text-white font-bold px-5 py-2.5 rounded-xl transition-colors text-sm">
            Start a Mail-In Repair <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Router component ──────────────────────────────────────────────────────────

export default function RepairBoardPage() {
  const { deviceSlug, boardOrCase } = useParams<{ deviceSlug: string; boardOrCase: string }>();

  const [taxonomy,   setTaxonomy]   = useState<KbTaxonomy>({ manufacturers: [], device_families: [], models: [], board_revisions: [] });
  const [repairCase, setRepairCase] = useState<RepairCase | null>(null);
  const [matchedBoardId, setMatchedBoardId] = useState<string | null>(null);
  const [mode, setMode] = useState<'loading' | 'board' | 'case' | 'notfound'>('loading');

  useEffect(() => {
    if (!deviceSlug || !boardOrCase) { setMode('notfound'); return; }

    fetchTaxonomy().then(async (tax) => {
      setTaxonomy(tax);
      const family = findFamily(tax, deviceSlug);
      if (!family) { setMode('notfound'); return; }

      const boards = revisionsForFamily(tax, family.id);
      const matchedBoard = boards.find(b =>
        b.name.toLowerCase().replace(/\s+/g, '-') === boardOrCase.toLowerCase()
      );

      if (matchedBoard) {
        setMatchedBoardId(matchedBoard.id);
        setMode('board');
      } else {
        try {
          const c = await fetchRepairCase(boardOrCase, false);
          setRepairCase(c);
          setMode('case');
        } catch {
          setMode('notfound');
        }
      }
    }).catch(() => setMode('notfound'));
  }, [deviceSlug, boardOrCase]);

  if (mode === 'loading') {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <Loader className="h-6 w-6 animate-spin text-gray-600" />
      </div>
    );
  }

  if (mode === 'notfound') {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Page not found.</p>
          <Link to="/repair-database" className="text-[#FF1744] text-sm hover:underline">← Repair Database</Link>
        </div>
      </div>
    );
  }

  if (mode === 'board') {
    return <RepairDevicePage deviceSlug={deviceSlug!} initialBoardId={matchedBoardId ?? undefined} />;
  }

  // case mode
  return <CasePage taxonomy={taxonomy} deviceSlug={deviceSlug!} repairCase={repairCase!} />;
}
